import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

// ════════════════════════════════════════════════════════════════
// TECH EDITORIAL MOTION: "BAGAIMANA AI TERBENTUK"
// Format: 2.5D Perspective Mockup + Grounded Artifacts + Fast Cuts
// Audio Sync: Whisper Transcribe (43.66s / 1310 frames @ 30fps)
// Safe Zone TikTok: Y 220px - 1520px
// ════════════════════════════════════════════════════════════════

export const AITechEditorialMotion: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const time = frame / fps;

  // Scene timing mapping based on voiceover audio
  let currentScene = 1;
  if (time >= 38.0) currentScene = 6;
  else if (time >= 33.5) currentScene = 5;
  else if (time >= 22.5) currentScene = 4;
  else if (time >= 15.0) currentScene = 3;
  else if (time >= 7.5) currentScene = 2;
  else currentScene = 1;

  // Kinetic subtitle text
  let currentSubtitle = "";
  let highlightedWord = "";

  if (time < 3.8) {
    currentSubtitle = "Sadar gak sih, komputer tercerdas di dunia hari ini...";
    highlightedWord = "KOMPUTER TERCERDAS";
  } else if (time < 7.5) {
    currentSubtitle = "Cara kerjanya mirip banget sama mesin tebak kelereng di pasar malam!";
    highlightedWord = "MESIN TEBAK KATA";
  } else if (time < 11.2) {
    currentSubtitle = "Dulu ilmuwan pusing bikin AI nulis jutaan aturan logika manual...";
    highlightedWord = "1.000.000+ ATURAN";
  } else if (time < 15.0) {
    currentSubtitle = "Dan hasilnya? Tetap kaku dan gampang error!";
    highlightedWord = "TETAP KAKU";
  } else if (time < 18.0) {
    currentSubtitle = "Sampai akhirnya, mereka balik cara pikirnya 180 derajat!";
    highlightedWord = "PARADIGM SHIFT";
  } else if (time < 22.5) {
    currentSubtitle = "Daripada diajarin mikir, mending mesinnya disuruh main tebak angka!";
    highlightedWord = "TEBAK PROBABILITAS";
  } else if (time < 28.0) {
    currentSubtitle = "Tiap lo ketik satu kata, dilempar kayak kelereng ngelewatin paku probabilitas...";
    highlightedWord = "TOKEN EMBEDDING";
  } else if (time < 33.5) {
    currentSubtitle = "Mesin gak punya kesadaran, cuma ngitung kata apa yang paling mungkin berikutnya!";
    highlightedWord = "NEXT-TOKEN PREDICTION";
  } else if (time < 38.0) {
    currentSubtitle = "Ketik 'Kucing makan...', sistem otomatis memilih kata skor tertinggi: 'IKAN'!";
    highlightedWord = "88.4% IKAN";
  } else {
    currentSubtitle = "AI bukan otak ajaib, tapi mesin statistik kata yang kelewat jago nebak!";
    highlightedWord = "STATISTIK, BUKAN MAGIC";
  }

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#07090E",
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
        color: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      {/* 0. AUDIO VO MASTERED VIA 9ROUTER */}
      <Audio src={staticFile("vo_kasino_kata_ai.mp3")} />

      {/* 1. EDITORIAL CYBER AMBIENCE (Strict Tech Palette) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(circle at 50% 15%, rgba(0, 240, 255, 0.08) 0%, transparent 60%),
            radial-gradient(circle at 85% 75%, rgba(245, 158, 11, 0.05) 0%, transparent 50%),
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: "100% 100%, 100% 100%, 50px 50px, 50px 50px",
        }}
      />

      {/* 2. TOP BRANDING BAR (Safe Zone Y: 240px - 290px) */}
      <div
        style={{
          position: "absolute",
          top: 240,
          left: 60,
          right: 60,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "rgba(13, 17, 24, 0.85)",
            backdropFilter: "blur(16px)",
            padding: "10px 22px",
            borderRadius: 30,
            border: "1px solid rgba(255, 255, 255, 0.12)",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.6)",
          }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: "#00F0FF",
              boxShadow: "0 0 10px #00F0FF",
            }}
          />
          <span style={{ fontSize: 22, fontWeight: 900, letterSpacing: 1.5, color: "#FFFFFF" }}>
            ZURA • AI LAB
          </span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 800,
              background: "#FF6B00",
              color: "#000",
              padding: "2px 8px",
              borderRadius: 6,
            }}
          >
            #TECH-EXPLAINER
          </span>
        </div>

        <div
          style={{
            background: "rgba(245, 158, 11, 0.15)",
            border: "1px solid rgba(245, 158, 11, 0.4)",
            color: "#FBBF24",
            padding: "8px 18px",
            borderRadius: 20,
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: 1,
          }}
        >
          ANATOMI AI
        </div>
      </div>

      {/* 3. DYNAMIC SCENE CONTENT (FAST-PACED GROUNDED VISUALS) */}

      {/* ═══════════ SCENE 1: HOOK (0s - 7.5s / f0 - f225) ═══════════ */}
      {currentScene === 1 && <Scene1HookEditorial frame={frame} fps={fps} />}

      {/* ═══════════ SCENE 2: KUTUKAN IF-ELSE (7.5s - 15s / f225 - f450) ═══════════ */}
      {currentScene === 2 && <Scene2VSCodeCollapse frame={frame - 225} fps={fps} />}

      {/* ═══════════ SCENE 3: PARADIGM SHIFT (15s - 22.5s / f450 - f675) ═══════════ */}
      {currentScene === 3 && <Scene3ProbabilityShift frame={frame - 450} fps={fps} />}

      {/* ═══════════ SCENE 4: MEKANISME PROBABILITAS (22.5s - 33.5s / f675 - f1005) ═══════════ */}
      {currentScene === 4 && <Scene4TokenGaltonBoard frame={frame - 675} fps={fps} />}

      {/* ═══════════ SCENE 5: NEXT-TOKEN KUCING MAKAN IKAN (33.5s - 38s / f1005 - f1140) ═══════════ */}
      {currentScene === 5 && <Scene5LiveCompletion frame={frame - 1005} fps={fps} />}

      {/* ═══════════ SCENE 6: OUTRO TAKEAWAY (38s - 43.6s / f1140 - f1310) ═══════════ */}
      {currentScene === 6 && <Scene6MasterTakeaway frame={frame - 1140} fps={fps} />}

      {/* 4. KINETIC SUBTITLE LOWER-THIRD (Safe Zone Y: 1280px - 1460px) */}
      <div
        style={{
          position: "absolute",
          bottom: 450,
          left: 60,
          right: 60,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          zIndex: 60,
        }}
      >
        {highlightedWord && (
          <div
            style={{
              background: "linear-gradient(90deg, #FF6B00, #F59E0B)",
              color: "#000000",
              fontWeight: 900,
              fontSize: 24,
              padding: "6px 20px",
              borderRadius: 8,
              letterSpacing: 2,
              marginBottom: 14,
              boxShadow: "0 6px 20px rgba(255, 107, 0, 0.4)",
              textTransform: "uppercase",
            }}
          >
            {highlightedWord}
          </div>
        )}
        <div
          style={{
            fontSize: 36,
            fontWeight: 800,
            lineHeight: 1.35,
            color: "#FFFFFF",
            textShadow: "0 4px 16px rgba(0,0,0,0.95)",
            background: "rgba(10, 12, 18, 0.82)",
            backdropFilter: "blur(14px)",
            padding: "16px 28px",
            borderRadius: 16,
            border: "1px solid rgba(255, 255, 255, 0.16)",
          }}
        >
          {currentSubtitle}
        </div>
      </div>

      {/* 5. CINEMATIC VIGNETTE */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          boxShadow: "inset 0 0 160px rgba(0, 0, 0, 0.85)",
          pointerEvents: "none",
        }}
      />
    </AbsoluteFill>
  );
};

// ════════════════════════════════════════════════════════════════
// SCENE 1: HOOK (2.5D Isometric AI Browser Mockup)
// ════════════════════════════════════════════════════════════════
const Scene1HookEditorial: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const tilt = spring({ frame, fps, config: { damping: 14, mass: 0.8 } });
  const rotX = interpolate(tilt, [0, 1], [24, 12]);
  const rotY = interpolate(tilt, [0, 1], [-18, -6]);

  const typeProgress = Math.min(1, frame / 50);
  const fullText = "Apa rahasia kecerdasan AI sebenarnya?";
  const typedChars = Math.floor(typeProgress * fullText.length);

  return (
    <div
      style={{
        position: "absolute",
        top: 360,
        left: 60,
        right: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontSize: 68,
          fontWeight: 900,
          textAlign: "center",
          lineHeight: 1.1,
          letterSpacing: -1,
          textTransform: "uppercase",
          marginBottom: 36,
        }}
      >
        KOMPUTER <span style={{ color: "#00F0FF", textShadow: "0 0 30px #00F0FF" }}>TERCERDAS</span>
        <br />
        CUMA <span style={{ color: "#F59E0B" }}>MESIN TEBAK?</span>
      </div>

      {/* 2.5D Browser Window Mockup */}
      <div
        style={{
          width: 880,
          background: "#0D1117",
          borderRadius: 24,
          border: "2px solid rgba(0, 240, 255, 0.4)",
          boxShadow:
            "0 30px 80px rgba(0, 0, 0, 0.8), 0 0 50px rgba(0, 240, 255, 0.15)",
          transform: `perspective(1200px) rotateX(${rotX}deg) rotateY(${rotY}deg) scale(0.98)`,
          overflow: "hidden",
        }}
      >
        {/* macOS Window Bar */}
        <div
          style={{
            background: "#161B22",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#FF5F56" }} />
          <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#FFBD2E" }} />
          <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#27C93F" }} />
          <div
            style={{
              marginLeft: 20,
              background: "#0D1117",
              padding: "6px 24px",
              borderRadius: 12,
              fontSize: 16,
              color: "#8B949E",
              fontFamily: "monospace",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            https://ai-intelligence.core/v1
          </div>
        </div>

        {/* Chat Interface Body */}
        <div style={{ padding: 36 }}>
          <div
            style={{
              background: "#161B22",
              borderRadius: 16,
              padding: 24,
              border: "1px solid rgba(0, 240, 255, 0.2)",
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "#00F0FF",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: 900,
                color: "#000",
                fontSize: 22,
              }}
            >
              Z
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#E6EDF3" }}>
              {fullText.slice(0, typedChars)}
              <span style={{ color: "#00F0FF", opacity: frame % 15 > 7 ? 1 : 0 }}>|</span>
            </div>
          </div>

          {/* Hologram Prediction Overlay */}
          <div
            style={{
              marginTop: 24,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "rgba(0, 240, 255, 0.05)",
              border: "1px dashed rgba(0, 240, 255, 0.3)",
              padding: "16px 24px",
              borderRadius: 14,
            }}
          >
            <div style={{ fontSize: 20, color: "#8B949E" }}>Status Arsitektur:</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#00F0FF",
                letterSpacing: 1.5,
              }}
            >
              PROBABILISTIC WORD CASINO
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// SCENE 2: KUTUKAN IF-ELSE (VS Code Scrolling & Error Crash)
// ════════════════════════════════════════════════════════════════
const Scene2VSCodeCollapse: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const shake = Math.sin(frame * 0.8) * 3;
  const stamp = spring({ frame, fps, config: { damping: 9, mass: 0.6 } });

  // Rapid code scroll
  const scrollOffset = (frame * 18) % 400;

  return (
    <div
      style={{
        position: "absolute",
        top: 350,
        left: 60,
        right: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontSize: 66,
          fontWeight: 900,
          textAlign: "center",
          lineHeight: 1.1,
          marginBottom: 30,
        }}
      >
        CARA LAMA: <span style={{ color: "#EF4444" }}>KUTUKAN IF-ELSE</span>
      </div>

      {/* Realistic VS Code Window */}
      <div
        style={{
          width: 880,
          height: 520,
          background: "#0D1117",
          borderRadius: 20,
          border: "2px solid #EF4444",
          boxShadow: "0 20px 60px rgba(239, 68, 68, 0.3)",
          overflow: "hidden",
          position: "relative",
          transform: `translateX(${shake}px)`,
        }}
      >
        {/* Editor Tab Bar */}
        <div
          style={{
            background: "#161B22",
            borderBottom: "1px solid #30363D",
            padding: "12px 24px",
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <div
            style={{
              background: "#0D1117",
              padding: "6px 18px",
              borderRadius: "8px 8px 0 0",
              borderTop: "2px solid #EF4444",
              color: "#F85149",
              fontSize: 16,
              fontWeight: 700,
              fontFamily: "monospace",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>ai_rules_manual_1956.py</span>
            <span style={{ color: "#EF4444" }}>● [ERRORS]</span>
          </div>
          <span style={{ color: "#8B949E", fontSize: 14, fontFamily: "monospace" }}>
            Lines: 1,420,890 (Impossible to Maintain)
          </span>
        </div>

        {/* Scrolling Code Content with Line Numbers */}
        <div
          style={{
            padding: "20px 30px",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 22,
            lineHeight: 1.6,
            color: "#C9D1D9",
            transform: `translateY(-${scrollOffset}px)`,
          }}
        >
          {[
            "def handle_human_language(input_text):",
            "    # JUTAWAN ATURAN MANUAL MUSTAHIL DICAKUP",
            '    if input_text == "kucing makan":',
            '        return "ikan"',
            '    elif input_text == "kucing tidur":',
            '        return "di kasur"',
            '    elif input_text == "kucing terbang":',
            '        raise ImpossibleBiologyError("Kucing gak punya sayap!")',
            '    elif input_text == "kucing makan rumput":',
            '        return "muntah bulu"',
            '    elif input_text == "kucing bermain":',
            '        return "bola benang"',
            '    elif input_text == "kucing ngomong":',
            '        return "meong"',
            "    # ERROR: Bahasa manusia terlalu fleksibel!",
            "    # CRASH: 100,000,000+ kombinasi kata terlewat!",
            "    return SystemFatalError()",
          ].map((line, idx) => (
            <div key={idx} style={{ display: "flex", gap: 20 }}>
              <span style={{ color: "#484F58", userSelect: "none" }}>{idx + 1042}</span>
              <span
                style={{
                  color: line.includes("def")
                    ? "#FF7B72"
                    : line.includes("return")
                    ? "#79C0FF"
                    : line.includes("ERROR") || line.includes("CRASH")
                    ? "#FFA657"
                    : "#D2A8FF",
                }}
              >
                {line}
              </span>
            </div>
          ))}
        </div>

        {/* Big Rejection Stamp Overlay */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: `translate(-50%, -50%) rotate(-10deg) scale(${stamp})`,
            background: "rgba(22, 27, 34, 0.96)",
            border: "8px solid #EF4444",
            padding: "20px 48px",
            borderRadius: 20,
            color: "#EF4444",
            fontSize: 54,
            fontWeight: 900,
            letterSpacing: 4,
            boxShadow: "0 20px 80px rgba(239, 68, 68, 0.8)",
            textAlign: "center",
          }}
        >
          TETAP KAKU & ERROR!
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// SCENE 3: PARADIGM SHIFT (Matrix Reversal & Probability)
// ════════════════════════════════════════════════════════════════
const Scene3ProbabilityShift: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const pop = spring({ frame, fps, config: { damping: 12, mass: 0.7 } });

  return (
    <div
      style={{
        position: "absolute",
        top: 360,
        left: 60,
        right: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontSize: 66,
          fontWeight: 900,
          textAlign: "center",
          lineHeight: 1.1,
          marginBottom: 36,
        }}
      >
        REVOLUSI: <span style={{ color: "#10B981" }}>BALIK CARA PIKIR 180°</span>
      </div>

      <div
        style={{
          width: 880,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          transform: `scale(${pop})`,
        }}
      >
        {/* Card Kiri: Pola Lama */}
        <div
          style={{
            background: "#161B22",
            borderRadius: 20,
            border: "2px solid rgba(239, 68, 68, 0.4)",
            padding: 36,
            textAlign: "center",
            opacity: 0.6,
          }}
        >
          <div style={{ fontSize: 20, color: "#EF4444", fontWeight: 800, marginBottom: 12 }}>
            POLA LAMA (1950 - 1990)
          </div>
          <div style={{ fontSize: 32, fontWeight: 900, color: "#FFFFFF", marginBottom: 16 }}>
            "AJARIN RUMUS"
          </div>
          <div style={{ fontSize: 20, color: "#8B949E", lineHeight: 1.5 }}>
            Menyuapi komputer dengan kamus dan hukum tata bahasa manual.
          </div>
        </div>

        {/* Card Kanan: Revolusi Baru */}
        <div
          style={{
            background: "linear-gradient(135deg, #064E3B 0%, #065F46 100%)",
            borderRadius: 20,
            border: "3px solid #10B981",
            padding: 36,
            textAlign: "center",
            boxShadow: "0 20px 60px rgba(16, 185, 129, 0.35)",
          }}
        >
          <div style={{ fontSize: 20, color: "#A7F3D0", fontWeight: 800, marginBottom: 12 }}>
            PARADIGMA MODERN (LLM)
          </div>
          <div style={{ fontSize: 32, fontWeight: 900, color: "#FFFFFF", marginBottom: 16 }}>
            "MAIN TEBAK ANGKA"
          </div>
          <div style={{ fontSize: 20, color: "#D1FAE5", lineHeight: 1.5 }}>
            Beri triliunan teks internet, biarkan mesin menghitung probabilitas statistik!
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// SCENE 4: TOKEN GALTON BOARD (Mekanisme Paku Probabilitas)
// ════════════════════════════════════════════════════════════════
const Scene4TokenGaltonBoard: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  return (
    <div
      style={{
        position: "absolute",
        top: 350,
        left: 60,
        right: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontSize: 64,
          fontWeight: 900,
          textAlign: "center",
          marginBottom: 24,
        }}
      >
        MEKANISME: <span style={{ color: "#00F0FF" }}>PAKU PROBABILITAS</span>
      </div>

      {/* 2.5D Galton Board / Probability Funnel UI */}
      <div
        style={{
          width: 880,
          background: "#0D1117",
          borderRadius: 24,
          border: "2px solid rgba(0, 240, 255, 0.4)",
          padding: "30px 40px",
          boxShadow: "0 25px 70px rgba(0, 240, 255, 0.2)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Token Pill Dispenser */}
        <div
          style={{
            background: "#1F2937",
            border: "2px solid #00F0FF",
            padding: "10px 30px",
            borderRadius: 30,
            fontSize: 24,
            fontWeight: 800,
            color: "#00F0FF",
            marginBottom: 20,
            boxShadow: "0 0 20px rgba(0, 240, 255, 0.4)",
          }}
        >
          INPUT TOKEN: ["KUCING", "MAKAN"]
        </div>

        {/* Dynamic Peg Matrix SVG */}
        <svg width="680" height="260" viewBox="0 0 680 260">
          {[0, 1, 2, 3, 4].map((row) =>
            Array.from({ length: row + 4 }).map((_, col) => {
              const cx = 340 + (col - (row + 4) / 2 + 0.5) * 65;
              const cy = 30 + row * 45;
              return (
                <circle
                  key={`peg-${row}-${col}`}
                  cx={cx}
                  cy={cy}
                  r="7"
                  fill="#F59E0B"
                  stroke="#FFE58F"
                  strokeWidth="2"
                />
              );
            })
          )}

          {/* Falling Token Marble Trails */}
          <circle
            cx={340 + Math.sin(frame * 0.2) * 50}
            cy={((frame * 16) % 220) + 20}
            r="16"
            fill="#00F0FF"
            filter="drop-shadow(0 0 16px #00F0FF)"
          />
        </svg>

        {/* Softmax Distribution Bar Graph */}
        <div
          style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 3fr 1fr 1fr",
            gap: 16,
            alignItems: "flex-end",
            height: 110,
            borderTop: "1px dashed rgba(255,255,255,0.15)",
            paddingTop: 16,
          }}
        >
          {[
            { word: "dan", height: 25, pct: "3%" },
            { word: "tidur", height: 35, pct: "7%" },
            { word: "IKAN", height: 95, pct: "88%", active: true },
            { word: "rumput", height: 30, pct: "5%" },
            { word: "batu", height: 15, pct: "0.1%" },
          ].map((bar, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: bar.height,
                  background: bar.active
                    ? "linear-gradient(180deg, #10B981, #047857)"
                    : "#1F2937",
                  borderRadius: "8px 8px 0 0",
                  border: bar.active ? "2px solid #34D399" : "none",
                  boxShadow: bar.active ? "0 0 24px rgba(16, 185, 129, 0.6)" : "none",
                }}
              />
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: bar.active ? "#10B981" : "#9CA3AF",
                }}
              >
                {bar.word}
              </span>
              <span style={{ fontSize: 14, color: "#6B7280" }}>{bar.pct}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// SCENE 5: LIVE TOKEN COMPLETION (Kucing Makan -> Ikan 88.4%)
// ════════════════════════════════════════════════════════════════
const Scene5LiveCompletion: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const pop = spring({ frame, fps, config: { damping: 11, mass: 0.6 } });

  return (
    <div
      style={{
        position: "absolute",
        top: 360,
        left: 60,
        right: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Input Prompt Card */}
      <div
        style={{
          width: 880,
          background: "#161B22",
          borderRadius: 20,
          border: "2px solid rgba(255, 255, 255, 0.2)",
          padding: "24px 36px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 28,
          boxShadow: "0 15px 40px rgba(0,0,0,0.6)",
        }}
      >
        <span style={{ fontSize: 26, color: "#8B949E", fontWeight: 700 }}>PROMPT:</span>
        <span style={{ fontSize: 40, fontWeight: 900, color: "#FFFFFF" }}>
          "Kucing makan..."
        </span>
        <span
          style={{
            background: "#00F0FF",
            color: "#000",
            fontSize: 16,
            fontWeight: 800,
            padding: "4px 14px",
            borderRadius: 8,
          }}
        >
          COMPLETION
        </span>
      </div>

      {/* 3 Ranked Next-Token Predictions */}
      <div
        style={{
          width: 880,
          display: "flex",
          flexDirection: "column",
          gap: 16,
          transform: `scale(${pop})`,
        }}
      >
        {/* Ranked #1: IKAN (88.4%) */}
        <div
          style={{
            background: "linear-gradient(90deg, #064E3B 0%, #065F46 100%)",
            border: "3px solid #10B981",
            borderRadius: 20,
            padding: "24px 36px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
            boxShadow: "0 15px 50px rgba(16, 185, 129, 0.45)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <span
                style={{
                  background: "#10B981",
                  color: "#000",
                  fontWeight: 900,
                  fontSize: 20,
                  padding: "4px 14px",
                  borderRadius: 8,
                }}
              >
                PILIHAN UTAMA AI
              </span>
              <span style={{ fontSize: 44, fontWeight: 900, color: "#FFFFFF" }}>"IKAN"</span>
            </div>
            <span style={{ fontSize: 42, fontWeight: 900, color: "#A7F3D0" }}>88.4%</span>
          </div>
          {/* Animated Bar */}
          <div
            style={{
              width: "100%",
              height: 12,
              background: "rgba(0,0,0,0.4)",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: "88.4%",
                height: "100%",
                background: "#34D399",
                borderRadius: 6,
                boxShadow: "0 0 16px #34D399",
              }}
            />
          </div>
        </div>

        {/* Ranked #2: rumput (7.2%) */}
        <div
          style={{
            background: "#161B22",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: 16,
            padding: "18px 30px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            opacity: 0.7,
          }}
        >
          <span style={{ fontSize: 32, fontWeight: 700, color: "#C9D1D9" }}>"rumput"</span>
          <span style={{ fontSize: 28, fontWeight: 800, color: "#8B949E" }}>7.2%</span>
        </div>

        {/* Ranked #3: batu (0.1%) */}
        <div
          style={{
            background: "#161B22",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: 16,
            padding: "18px 30px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            opacity: 0.45,
          }}
        >
          <span style={{ fontSize: 32, fontWeight: 700, color: "#8B949E" }}>"batu"</span>
          <span style={{ fontSize: 28, fontWeight: 800, color: "#484F58" }}>0.1%</span>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// SCENE 6: MASTER TAKEAWAY (Editorial Architecture Outro)
// ════════════════════════════════════════════════════════════════
const Scene6MasterTakeaway: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const pop = spring({ frame, fps, config: { damping: 11, mass: 0.7 } });

  return (
    <div
      style={{
        position: "absolute",
        top: 350,
        left: 60,
        right: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontSize: 70,
          fontWeight: 900,
          textAlign: "center",
          lineHeight: 1.1,
          letterSpacing: -1,
          marginBottom: 32,
          transform: `scale(${pop})`,
        }}
      >
        BUKAN OTAK AJAIB...
        <br />
        <span
          style={{
            background: "linear-gradient(90deg, #F59E0B, #FF6B00)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textShadow: "0 10px 40px rgba(245, 158, 11, 0.4)",
          }}
        >
          MESIN STATISTIK KATA!
        </span>
      </div>

      {/* Summary Bento Architecture Card */}
      <div
        style={{
          width: 880,
          background: "#0D1117",
          borderRadius: 24,
          border: "2px solid rgba(245, 158, 11, 0.5)",
          padding: 36,
          boxShadow: "0 25px 70px rgba(245, 158, 11, 0.2)",
          marginBottom: 36,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 20,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              background: "#161B22",
              padding: "20px 24px",
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <div style={{ color: "#8B949E", fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
              KATA KUNCI 01
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: "#00F0FF" }}>
              Next-Token Prediction
            </div>
          </div>

          <div
            style={{
              background: "#161B22",
              padding: "20px 24px",
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <div style={{ color: "#8B949E", fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
              KATA KUNCI 02
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: "#F59E0B" }}>
              Probabilitas Statistik
            </div>
          </div>
        </div>

        <div style={{ fontSize: 22, color: "#E6EDF3", lineHeight: 1.5, textAlign: "center" }}>
          AI tidak memiliki pemikiran sadar — ia hanya menghitung probabilitas kata berikutnya yang
          paling masuk akal berdasarkan jutaan data latihan.
        </div>
      </div>

      {/* Call to Action Follow Pill */}
      <div
        style={{
          background: "linear-gradient(90deg, #FF6B00, #F59E0B)",
          color: "#000000",
          fontWeight: 900,
          fontSize: 26,
          padding: "18px 44px",
          borderRadius: 40,
          boxShadow: "0 10px 40px rgba(255, 107, 0, 0.5)",
          letterSpacing: 1.5,
        }}
      >
        FOLLOW @zura.ailab UNTUK EPS 02!
      </div>
    </div>
  );
};
